import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabaseClient';
import ProposalModal from './ProposalModal';
import WorkNotes from './WorkNotes';
import { useAccess } from '../lib/access';

const STATUS_COLORS = {
  Pending: '#e8a838',
  Approved: '#2D8A6B',
  Rejected: '#d4614a',
};

export default function JobDetail({ job, photos = [], onClose, onJobUpdated, onConvertToTask, onPhotosChanged }) {
  const { email } = useAccess();
  const [proposals, setProposals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showProposalModal, setShowProposalModal] = useState(false);
  const [editingProposal, setEditingProposal] = useState(null);
  const [lightboxIndex, setLightboxIndex] = useState(-1);
  const [uploading, setUploading] = useState(false);

  const fetchProposals = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('proposals')
      .select('*')
      .eq('job_id', job.id)
      .order('total_cost', { ascending: true });
    if (data) setProposals(data);
    if (error) console.error(error);
    setLoading(false);
  }, [job.id]);

  useEffect(() => {
    fetchProposals();
  }, [fetchProposals]);

  const lowestCost = proposals.length > 0
    ? Math.min(...proposals.map(p => parseFloat(p.total_cost)))
    : null;

  const handleApprove = async (proposalId) => {
    for (const p of proposals) {
      if (p.id === proposalId) {
        await supabase.from('proposals').update({ status: 'Approved' }).eq('id', p.id);
      } else if (p.status === 'Approved') {
        await supabase.from('proposals').update({ status: 'Pending' }).eq('id', p.id);
      }
    }
    await supabase.from('jobs').update({ status: 'Approved', updated_at: new Date().toISOString() }).eq('id', job.id);
    await fetchProposals();
    if (onJobUpdated) onJobUpdated();
  };

  const handleReject = async (proposalId) => {
    await supabase.from('proposals').update({ status: 'Rejected' }).eq('id', proposalId);
    await fetchProposals();
  };

  const handleDeleteProposal = async (proposalId) => {
    if (!window.confirm('Delete this proposal?')) return;
    await supabase.from('proposals').delete().eq('id', proposalId);
    setProposals(proposals.filter(p => p.id !== proposalId));
  };

  const onProposalSaved = (saved, isNew) => {
    if (isNew) {
      setProposals(prev => [...prev, saved].sort((a, b) => a.total_cost - b.total_cost));
    } else {
      setProposals(prev => prev.map(p => p.id === saved.id ? saved : p));
    }
    setShowProposalModal(false);
    setEditingProposal(null);
  };

  const handleQuickUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    setUploading(true);
    for (const file of files) {
      const path = `${job.id}/${Date.now()}-${file.name}`;
      const { error } = await supabase.storage.from('job-photos').upload(path, file);
      if (!error) {
        await supabase.from('job_photos').insert({
          job_id: job.id,
          storage_path: path,
          filename: file.name,
        });
      }
    }
    setUploading(false);
    if (onPhotosChanged) onPhotosChanged();
    e.target.value = '';
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal job-detail-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{job.title}</h3>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>

        <div className="modal-body">
          <div className="job-detail-meta">
            {job.properties?.name && (
              <span className="tag tag-property">{job.properties.name}</span>
            )}
            <span className="tag tag-category">{job.category}</span>
            <span className={`tag tag-job-status status-${job.status?.replace(/\s/g, '-')}`}>
              {job.status}
            </span>
          </div>

          {job.description && (
            <p className="job-detail-desc">{job.description}</p>
          )}

          {/* Photo Gallery */}
          <div className="job-photos-section">
            <div className="proposals-header">
              <h4>Photos ({photos.length})</h4>
              <label className="btn btn-secondary btn-sm" style={{ cursor: 'pointer', marginBottom: 0 }}>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleQuickUpload}
                  style={{ display: 'none' }}
                />
                {uploading ? 'Uploading...' : '+ Add Photos'}
              </label>
            </div>

            {photos.length > 0 ? (
              <div className="job-photo-grid">
                {photos.map((photo, i) => (
                  <div
                    key={photo.id}
                    className="job-photo-thumb"
                    onClick={() => setLightboxIndex(i)}
                  >
                    <img src={photo.url} alt={photo.filename || 'Job photo'} />
                  </div>
                ))}
              </div>
            ) : (
              <p style={{ fontSize: 13, color: '#999', margin: '8px 0 0' }}>
                No photos yet — add photos to document the job scope
              </p>
            )}
          </div>

          <div style={{ marginTop: 20 }}>
            <WorkNotes jobId={job.id} authorEmail={email} />
          </div>

          <div className="proposals-header" style={{ marginTop: 20 }}>
            <h4>Proposals ({proposals.length})</h4>
            <button
              className="btn btn-primary btn-sm"
              onClick={() => { setEditingProposal(null); setShowProposalModal(true); }}
            >
              + Add Proposal
            </button>
          </div>

          {loading ? (
            <p className="job-detail-loading">Loading proposals...</p>
          ) : proposals.length === 0 ? (
            <div className="empty-state" style={{ padding: '30px 20px' }}>
              <h3>No proposals yet</h3>
              <p>Add a proposal to start comparing bids</p>
            </div>
          ) : (
            <div className="proposals-table-wrap">
              <table className="proposals-table">
                <thead>
                  <tr>
                    <th>Vendor</th>
                    <th>Cost</th>
                    <th>Days</th>
                    <th>Includes</th>
                    <th>Excludes</th>
                    <th>Warranty</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {proposals.map(p => {
                    const isLowest = parseFloat(p.total_cost) === lowestCost;
                    const isApproved = p.status === 'Approved';
                    return (
                      <tr
                        key={p.id}
                        className={`proposal-row ${isApproved ? 'approved' : ''}`}
                      >
                        <td className="proposal-vendor">{p.vendor_name}</td>
                        <td className={`proposal-cost ${isLowest ? 'lowest' : ''}`}>
                          ${parseFloat(p.total_cost).toLocaleString()}
                        </td>
                        <td>{p.estimated_days || '—'}</td>
                        <td className="proposal-detail">{p.includes || '—'}</td>
                        <td className="proposal-detail">{p.excludes || '—'}</td>
                        <td>{p.warranty || '—'}</td>
                        <td>
                          <span
                            className="proposal-status-badge"
                            style={{ background: STATUS_COLORS[p.status] || '#999' }}
                          >
                            {p.status}
                          </span>
                        </td>
                        <td className="proposal-actions">
                          {p.status !== 'Approved' && (
                            <button
                              className="btn btn-sm btn-approve"
                              onClick={() => handleApprove(p.id)}
                              title="Approve"
                            >
                              Approve
                            </button>
                          )}
                          {p.status === 'Approved' && onConvertToTask && (
                            <button
                              className="btn btn-sm btn-convert"
                              onClick={() => onConvertToTask(job, p)}
                              title="Convert to Task"
                            >
                              Convert to Task
                            </button>
                          )}
                          {p.status !== 'Rejected' && p.status !== 'Approved' && (
                            <button
                              className="btn btn-sm btn-reject"
                              onClick={() => handleReject(p.id)}
                              title="Reject"
                            >
                              Reject
                            </button>
                          )}
                          <button
                            className="icon-btn"
                            onClick={() => { setEditingProposal(p); setShowProposalModal(true); }}
                            title="Edit"
                          >
                            Edit
                          </button>
                          <button
                            className="icon-btn delete"
                            onClick={() => handleDeleteProposal(p.id)}
                            title="Delete"
                          >
                            Del
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {job.notes && (
            <div className="job-detail-notes">
              <strong>Notes:</strong> {job.notes}
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Close</button>
        </div>
      </div>

      {showProposalModal && (
        <ProposalModal
          proposal={editingProposal}
          jobId={job.id}
          onSaved={onProposalSaved}
          onClose={() => { setShowProposalModal(false); setEditingProposal(null); }}
        />
      )}

      {/* Lightbox */}
      {lightboxIndex >= 0 && photos.length > 0 && (
        <div className="lightbox-overlay" onClick={() => setLightboxIndex(-1)}>
          <div className="lightbox-content" onClick={e => e.stopPropagation()}>
            <button className="lightbox-close" onClick={() => setLightboxIndex(-1)}>&times;</button>
            <img
              className="lightbox-image"
              src={photos[lightboxIndex].url}
              alt={photos[lightboxIndex].filename || 'Photo'}
            />
            {photos.length > 1 && (
              <div className="lightbox-nav">
                <button
                  className="lightbox-arrow"
                  onClick={() => setLightboxIndex((lightboxIndex - 1 + photos.length) % photos.length)}
                >&lsaquo;</button>
                <span className="lightbox-counter">
                  {lightboxIndex + 1} of {photos.length}
                </span>
                <button
                  className="lightbox-arrow"
                  onClick={() => setLightboxIndex((lightboxIndex + 1) % photos.length)}
                >&rsaquo;</button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
